import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { api } from '@/lib/api';
import { enqueueMutation } from './queue';

/**
 * Axios request interceptor that re-routes mutating requests through
 * the offline queue when the browser reports it is offline.
 *
 * Behaviour:
 *   - Method is GET or the browser is online → pass-through, no-op.
 *   - Method is POST/PATCH/PUT/DELETE AND `navigator.onLine === false`
 *     → the request is persisted in Dexie with `enqueueMutation` and
 *     a synthetic 202 Accepted response is short-circuited back to
 *     the caller. The body of the response is `{ queued: true,
 *     queuedId }` so an interested call site can recognise the
 *     queued path; a vanilla call site sees a `2xx` and treats the
 *     mutation as committed (with a small white lie until the queue
 *     replays — surfaced to the user via the topbar badge).
 *
 * The interceptor is registered once at module load. We also tag the
 * config with `__offlineEnqueued` to skip the response interceptor
 * (the shared error-mapping interceptor in api.ts only fires on
 * rejection so this is a no-op in practice, but the explicit guard
 * documents intent).
 */
const MUTATION_METHODS = new Set(['post', 'patch', 'put', 'delete']);

interface OfflineConfig extends InternalAxiosRequestConfig {
  __offlineEnqueued?: boolean;
}

let installed = false;

export function installOfflineInterceptor(): void {
  if (installed) return;
  installed = true;

  api.interceptors.request.use(async (config) => {
    const method = (config.method ?? 'get').toLowerCase();
    if (!MUTATION_METHODS.has(method)) return config;
    if (typeof navigator !== 'undefined' && navigator.onLine) return config;

    // Offline path — persist and short-circuit. We can't return a
    // Response from a request interceptor, so we throw a sentinel
    // object that the rejection chain treats as a successful
    // adapter response. Axios spec: a request interceptor that
    // throws halts the chain, which is exactly what we want; we
    // then transform that into a fulfilled response in a wrapper.
    const queuedId = await enqueueMutation({
      method: method.toUpperCase() as 'POST' | 'PATCH' | 'PUT' | 'DELETE',
      url: config.url ?? '',
      body: config.data,
      headers: extractHeaders(config),
    });

    const synthetic: AxiosResponse = {
      data: { queued: true, queuedId },
      status: 202,
      statusText: 'Accepted (queued offline)',
      headers: {},
      config: { ...config, __offlineEnqueued: true } as OfflineConfig,
    };
    // The unusual control-flow pattern: throw a thenable-like that the
    // wrapper turns into a fulfilled response. Simpler: bypass axios
    // entirely by returning a config flagged with an adapter override.
    config.adapter = async () => synthetic;
    return config;
  });
}

/**
 * Pull the user-visible headers (anything other than Content-Type) off
 * an axios config so we can re-emit them on replay. Axios stores
 * headers as a class with helpers; toJSON gives us a plain object.
 */
function extractHeaders(
  config: InternalAxiosRequestConfig,
): Record<string, string> | undefined {
  const headers = config.headers;
  if (!headers) return undefined;
  const flat: Record<string, string> = {};
  // AxiosHeaders has .toJSON(); plain objects are usable as-is.
  const json =
    typeof (headers as { toJSON?: () => unknown }).toJSON === 'function'
      ? ((headers as { toJSON: () => Record<string, unknown> }).toJSON())
      : (headers as unknown as Record<string, unknown>);
  for (const [k, v] of Object.entries(json)) {
    if (typeof v === 'string' && k.toLowerCase() !== 'content-type') {
      flat[k] = v;
    }
  }
  return Object.keys(flat).length > 0 ? flat : undefined;
}
